import {
  useEvent,
  useEventsOnViewLoad
} from '@emerald-works/react-event-bus-client'
import { ActionButton } from '@emerald-works-nova/components/dist/components'
import {
  Grid,
  Typography,
  Paper,
  CircularProgress,
  TextField,
  Switch
} from '@mui/material'
import React from 'react'
import { useStyles } from './style'
import PlanSlice from '../../reducers/plans'
import { useDispatch, useSelector } from 'react-redux'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { setPlans } from '../../reducers/plans/slice'
import { BannerStatic } from '../../components'

const PlanList = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const plans = useSelector(PlanSlice.selectors.selectPlans)

  const { handleSubmit, reset, control, formState } = useForm()
  const { fields } = useFieldArray({
    control,
    name: 'plans'
  })
  const handleSwitch = (onChange) => {
    return (event) => {
      onChange(event.target.checked)
    }
  }

  const setPlansForm = (payload) => {
    const plans = payload.map((plan) => ({
      code: plan.sk,
      description: plan.description,
      active: plan.active,
      planCode: plan.planCode
    }))

    reset({
      plans
    })
  }

  const [getPlansEvent, updatePlansEvent] = useEvent([
    {
      eventName: 'subscriptionGetProducts',
      onSuccess: (payload) => {
        dispatch(setPlans(payload))
        setPlansForm(payload)
      },
      onError: (error) => {
        console.error('subscriptionGetProducts error', error)
      }
    },
    {
      eventName: 'subscriptionUpdateProducts',
      onSuccess: (payload) => {
        dispatch(setPlans(payload))
        setPlansForm(payload)
        BannerStatic.show({ label: 'Plans updated successfully' })
      },
      onError: (error) => {
        console.error('subscriptionUpdateProducts error', error)
        const message =
          error.code === 'BAD_REQUEST_ERROR'
            ? error.description
            : 'Unable to update plans'
        BannerStatic.show({ label: message, isError: true })
      }
    }
  ])

  useEventsOnViewLoad(() => {
    getPlansEvent.trigger()
  }, [])

  const onSubmit = (data) => {
    updatePlansEvent.trigger({ plans: data.plans })
  }

  return (
    <Grid mb={4}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container classes={{ root: classes.header }}>
          <Grid container direction='row' alignItems='center'>
            <Grid item style={{ flexGrow: 1 }}>
              <Typography
                id='plan-list-title'
                tabIndex={0}
                component='div'
                variant='h5'
              >
                Checkout Plans Options
                <Typography
                  component='span'
                  variant='subtitle1'
                  className={classes.contentTotal}
                >
                  ({plans.length})
                </Typography>
              </Typography>
            </Grid>
            <Grid item>
              <ActionButton
                id='save-plans-button'
                variant='primary'
                name='save-plans'
                aria-label='save plans'
                type='submit'
                startIcon={
                  updatePlansEvent.isWorking && <CircularProgress size={15} />
                }
                disabled={updatePlansEvent.isWorking || !formState.isValid}
              >
                Save Plans
              </ActionButton>
            </Grid>
          </Grid>
        </Grid>

        <Grid>
          <Typography fontSize={12} mb={4}>
            Complete the plan code fields with the Recurly plan codes that will
            be displayed during the checkout process. Inactive plans won't be
            displayed to users.
          </Typography>
          <Paper
            id='plan-list'
            classes={{ root: classes.tablePaper }}
            tabIndex={0}
            aria-label='Plan List'
            elevation={0}
          >
            <Grid
              id='plans-list-headers'
              container
              tabIndex={0}
              gap={2}
              direction='row'
              alignItems='center'
              className={classes.gridHeader}
              classes={{ root: classes.tableHead }}
              aria-label='Code, Short Description, Plan Code, Status'
            >
              <Grid item md={1.5} xs={12}>
                Name
              </Grid>
              <Grid item md={4} xs={12}>
                Short Description
              </Grid>
              <Grid item md={3.5} xs={12}>
                Plan Code
              </Grid>
              <Grid item md={2} xs={12}>
                Status
              </Grid>
            </Grid>
            {getPlansEvent.isWorking ? (
              <Grid
                id='plan-list-loading'
                container
                direction='row'
                justifyContent='center'
                alignItems='center'
                classes={{ root: classes.loadingAnimation }}
              >
                <CircularProgress size={60} color='primary' />
              </Grid>
            ) : (
              <>
                {fields.map((plan, index) => (
                  <Grid
                    key={index}
                    id='plans-list-body'
                    gap={2}
                    container
                    tabIndex={0}
                    direction='row'
                    alignItems='center'
                    className={classes.gridHeader}
                    classes={{ root: classes.items }}
                    aria-label='Short Description, Plan Code, Status'
                  >
                    <Grid item md={1.5} xs={12}>
                      <Typography data-test={`plans.${index}.name`}>
                        Plan {index + 1}
                      </Typography>
                    </Grid>
                    <Grid item md={4} xs={12}>
                      <Controller
                        name={`plans.${index}.description`}
                        rules={{ required: true }}
                        control={control}
                        defaultValue={plan?.description}
                        render={({ field: { name, onChange, value } }) => (
                          <TextField
                            onChange={onChange}
                            name={name}
                            data-test={`plans.${index}.description`}
                            value={value}
                            size='small'
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item md={3.5} xs={12}>
                      <Controller
                        name={`plans.${index}.planCode`}
                        rules={{ required: true }}
                        control={control}
                        defaultValue={plan?.planCode}
                        render={({ field: { name, onChange, value } }) => (
                          <TextField
                            onChange={onChange}
                            name={name}
                            data-test={`plans.${index}.planCode`}
                            value={value}
                            size='small'
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item md={2} xs={12}>
                      <Controller
                        name={`plans.${index}.active`}
                        control={control}
                        defaultValue={plan?.active}
                        render={({ field: { name, onChange, value } }) => (
                          <Grid
                            alignItems='center'
                            justifyContent='start'
                            container
                          >
                            <Typography>
                              {value ? 'Active' : 'Inactive'}
                            </Typography>
                            <Switch
                              name={name}
                              checked={value}
                              data-test={`plans.${index}.active`}
                              onChange={handleSwitch(onChange)}
                            />
                          </Grid>
                        )}
                      />
                    </Grid>
                  </Grid>
                ))}
              </>
            )}
          </Paper>
        </Grid>
      </form>
    </Grid>
  )
}

export default PlanList
